// src/pages/dashboards/admin/AdminCompanies.jsx
// Company list with search + create. Row opens the detail modal.

import React, { useEffect, useState, useCallback } from "react";
import { listCompanies } from "../../../api/admin";
import { Card, Input, Button, Badge, Loading, EmptyState } from "../../../components/ui";
import { Search, Plus, Building2 } from "../../../components/icons";
import { date, titleCase } from "../../../lib/format";
import CreateCompanyModal from "./CreateCompanyModal";
import CompanyDetailModal from "./CompanyDetailModal";

const STATUS_TONE = { active: "green", cancelled: "gray", expired: "red", past_due: "amber" };
const PAGE = 50;

export default function AdminCompanies() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, offset };
      if (q.trim()) params.q = q.trim();
      setRows(await listCompanies(params));
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [q, offset]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold">Companies</h1>
        <Button onClick={() => setCreating(true)}><Plus size={16} /> New company</Button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
        <Input value={q} onChange={(e) => { setOffset(0); setQ(e.target.value); }} placeholder="Search name or email" className="pl-9" />
      </div>

      <Card>
        {loading ? (
          <Loading label="Loading companies" />
        ) : rows.length === 0 ? (
          <EmptyState title="No companies yet" hint="Create one to get started." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Company</th>
                  <th className="px-4 py-3 font-medium">Tier</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Admins</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Users</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Outstanding</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Term ends</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setOpenId(c.id)}
                    className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0
                               hover:bg-tb-bg-2 dark:hover:bg-tb-d-card cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="grid place-items-center h-8 w-8 rounded-lg bg-tb-bg-2 dark:bg-tb-d-card text-tb-muted shrink-0">
                          <Building2 size={16} />
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{c.name}</p>
                          {c.email && <p className="text-xs text-tb-muted truncate">{c.email}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{titleCase(c.tier)}</td>
                    <td className="px-4 py-3"><Badge tone={STATUS_TONE[c.status]}>{titleCase(c.status)}</Badge></td>
                    <td className="px-4 py-3 hidden md:table-cell">{c.admins}</td>
                    <td className="px-4 py-3 hidden md:table-cell">{c.linked_users}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {c.claims_outstanding > 0
                        ? <Badge tone="amber">{c.claims_outstanding}</Badge>
                        : <span className="text-tb-muted">0</span>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-tb-muted">{date(c.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between mt-4">
        <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>Previous</Button>
        <span className="text-sm text-tb-muted">{rows.length ? `${offset + 1}–${offset + rows.length}` : "—"}</span>
        <Button variant="ghost" disabled={rows.length < PAGE} onClick={() => setOffset((o) => o + PAGE)}>Next</Button>
      </div>

      {creating && <CreateCompanyModal onClose={() => setCreating(false)} onCreated={() => load()} />}
      {openId && <CompanyDetailModal companyId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}